import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukDayTime } from "@/lib/time";
import { Btn, CellSub, Empty, Note, Panel, St } from "@/components/portal/crm/kit";

const STAGE_TONE = { completed: "mint", declined: "red", offer_made: "blue", in_review: "amber" };
const ROLE_TONE = { Connexis: "ink", Lender: "blue", Introducer: "mint" };

const Thread = ({ notes, testId }) => (
  <div style={{ display: "grid", gap: 12, marginTop: 14 }} data-testid={testId}>
    {notes.map((n) => (
      <div key={n.id} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}
           data-testid={`${testId}-note-${n.id}`}>
        <St tone={ROLE_TONE[n.role_label] || "slate"}>{n.role_label}</St>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13.5, color: "var(--ink-3)", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{n.body}</div>
          <CellSub style={{ marginTop: 4 }}>{ukDayTime(n.created_at, "")}</CellSub>
        </div>
      </div>
    ))}
  </div>
);

/** What is being said on the cases your recruited partners brought in — the feedback, not the client. */
export function RecruitCaseFeedback() {
  const [d, setD] = useState(null);
  const [who, setWho] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let live = true;
    api.get(`/refer/network/case-feedback${who ? `?recruit_id=${who}` : ""}`)
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) setD({ items: [], recruits: [] }); });
    return () => { live = false; };
  }, [who]);

  if (!d) return <Empty testId="recruit-feedback-loading">Reading your partners' cases…</Empty>;

  const recruits = d.recruits || [];
  if (!recruits.length) {
    return (
      <Panel testId="recruit-feedback-none">
        <Empty testId="recruit-feedback-empty-partners">
          Nothing here yet — bring a partner on board and you'll be able to follow how their cases are
          going without having to ask them.
        </Empty>
      </Panel>
    );
  }

  return (
    <Panel
      testId="recruit-feedback"
      title="What's being said on your partners' cases"
      sub={d.note}
      pad={false}
      right={recruits.length > 1 ? (
        <select className="inp" value={who} onChange={(e) => { setWho(e.target.value); setOpen(null); }}
                style={{ width: "auto", height: 34, paddingRight: 32 }} data-testid="recruit-feedback-filter">
          <option value="">All {recruits.length} partners</option>
          {recruits.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      ) : null}
      footer={<span>{d.items.length} case{d.items.length === 1 ? "" : "s"} · {d.notes_total} note{d.notes_total === 1 ? "" : "s"} you can read</span>}
    >
      {d.items.length === 0 ? (
        <Empty testId="recruit-feedback-empty">
          Your partners haven't put a case in yet. The moment they do, every note the desks and
          Connexis leave on it shows up here.
        </Empty>
      ) : (
        <div>
          {d.items.map((c) => {
            const isOpen = open === c.id;
            const live = (c.routes || []).filter((r) => r.status !== "declined").length;
            return (
              <div key={c.id} style={{ borderTop: "1px solid var(--line-2)", padding: "16px 22px" }}
                   data-testid={`recruit-case-${c.id}`}>
                <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                  <St tone={STAGE_TONE[c.stage] || "slate"}>{(c.stage || "new").replace(/_/g, " ")}</St>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink-3)" }}>
                      {c.partner_name} · {c.finance_type || "Business finance"}
                    </div>
                    <CellSub>
                      {gbp(c.amount_gbp)} · {c.reference || "no reference yet"} ·{" "}
                      {live} desk{live === 1 ? "" : "s"} on it
                      {c.last_said_at ? ` · last said ${ukDayTime(c.last_said_at, "")}` : " · nothing said yet"}
                    </CellSub>
                  </div>
                  <Btn sm onClick={() => setOpen(isOpen ? null : c.id)} testId={`recruit-case-toggle-${c.id}`}>
                    {isOpen ? "Hide" : `Read ${c.notes.length || "the"} note${c.notes.length === 1 ? "" : "s"}`}
                  </Btn>
                </div>
                {isOpen && (
                  c.notes.length
                    ? <Thread notes={c.notes} testId={`recruit-thread-${c.id}`} />
                    : <Note>Nothing has been written on this one yet.</Note>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
